import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { PersonaTipoEnum } from '../../api/generated';
import { AppMainSection } from '../../model/app-main-section';
import appRoutes from '../../routing/app-routes';
import { UserState, openidSSO, getStatusUtenza } from '../../store';
import {
  setIsMifidCurrentPage,
  setQuitMifid,
} from '../../store/features/home/onboarding-self';
import {
  setAmlFatcaCurrentPage,
  setQuitAmlFatca,
} from '../../store/features/home/onboarding-self/home-onboarding-self-aml-fatca-crs-survey-slice';
import { useAppSelector } from '../redux';
import routeSectionMap from './utils/route-section-map';
import { env } from '../../env';
import { useAppNavigation } from '../../utils/navigation.utils';

export interface HomeLogicResult {
  selectedSection?: AppMainSection;
  onSelectedSectionChanged: (section?: AppMainSection) => void;
  onProfileIconPress: () => void;
  onHelpIconPress: () => void;
  enableMarketplaceMobile?: boolean;
  isProspect: boolean;
  isFiduciaria: boolean;
  setQuitMifid: ActionCreatorWithPayload<any, string>;
  setQuitAmlFatca: ActionCreatorWithPayload<any, string>;
}

export const useHomeLogic = (): HomeLogicResult => {
  const dispatch = useDispatch();
  const { appNavigate, pathname } = useAppNavigation();
  const {
    informativeMode,
    isProspect,
    enableMarketplaceMobile,
    userType,
    fiduciaria,
  } = useAppSelector<UserState>((state) => state.data.user);

  /**
   * Whenever the path changes, is checked if one of the home sections correspondes to the new path
   * and in that case set it as the selected one
   */
  const selectedSection = useMemo(
    (): AppMainSection | undefined => routeSectionMap.sectionForRoute(pathname),
    [pathname]
  );

  /**
   * Whenever the user select a new home section, a new navigation to the corresponding path
   * is triggered
   */
  const onSelectedSectionChanged = useCallback(
    (section: AppMainSection) => {
      if (selectedSection === section) {
        return;
      }
      appNavigate(
        routeSectionMap.routeForSection(section) || appRoutes.NotFound
      );
    },
    [dispatch, selectedSection]
  );

  useEffect(() => {
    if (pathname === '/home') {
      appNavigate(appRoutes.Dashboard, { replace: true });
    }
    if (
      informativeMode &&
      (selectedSection === 'documents' || selectedSection === 'sign')
    ) {
      appNavigate(appRoutes.Dashboard, { replace: true });
    }
  }, [pathname, selectedSection]);

  useEffect(() => {
    if (
      enableMarketplaceMobile !== undefined &&
      enableMarketplaceMobile !== null &&
      userType === PersonaTipoEnum.Pg &&
      env.appModule === 'mobile'
    ) {
      dispatch(openidSSO());
    }
  }, [enableMarketplaceMobile]);
  const onProfileIconPress = useCallback(() => {
    onSelectedSectionChanged('profile');
  }, [onSelectedSectionChanged]);

  const onHelpIconPress = useCallback(() => {
    onSelectedSectionChanged('faqs');
  }, [onSelectedSectionChanged]);

  useEffect(() => {
    dispatch(
      setIsMifidCurrentPage(pathname?.includes(appRoutes.MifidSurveyPage))
    );
    dispatch(
      setAmlFatcaCurrentPage(pathname === appRoutes.AmlFatcaCrsSurveyPage)
    );
  }, [dispatch, pathname]);

  const pollCountRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    const statusUtenzaRequest = () => {
      if (pollCountRef.current < 5) {
        dispatch(getStatusUtenza());
        pollCountRef.current += 1;
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }
    };

    statusUtenzaRequest();
    intervalRef.current = setInterval(statusUtenzaRequest, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [dispatch]);

  return {
    selectedSection,
    onSelectedSectionChanged,
    onProfileIconPress,
    onHelpIconPress,
    isProspect,
    setQuitMifid,
    setQuitAmlFatca,
    isFiduciaria: fiduciaria,
  };
};
